import BookingWhenList from '@/features/reservations/components/BookingWhenList';
import CreateDetailsSection from '@/features/reservations/components/CreateDetailsSection';
import CreateGuestSection from '@/features/reservations/components/CreateGuestSection';
import { useBookingLimits } from '@/features/reservations/hooks/useBookingLimits';
import { useCreateReservation } from '@/features/reservations/hooks/useReservationMutations';
import {
  createBody,
  createProblem,
  createValues,
  hasDraft,
} from '@/features/reservations/model/reservation-form';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useT } from '@/i18n';
import { useContractForm } from '@/lib/forms/useContractForm';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { todayString } from '@/utils/datetime';
import { sectionRouter } from '@/utils/navigateMain';
import { setUnsavedDirty } from '@/utils/unsavedChanges';
import type { StaffReservationCreateBody } from '@restaurant/api-client';
import { StaffReservationCreateSchema } from '@restaurant/api-contracts';
import {
  BlockTitle,
  Link as F7Link,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useRef, useState } from 'react';
import { useController } from 'react-hook-form';

function CreateReservationPage() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const tr = useT();
  const createMutation = useCreateReservation();
  // The booking defaults to today; remember that so an unchanged date doesn't
  // count as a draft edit (see hasDraft).
  const start = useRef(createValues(todayString())).current;
  const form = useContractForm(StaffReservationCreateSchema, {
    defaultValues: start,
    toBody: createBody,
  });
  const { control } = form;
  const { field: date } = useController({ control, name: 'date' });
  const { field: time } = useController({ control, name: 'time' });
  const { field: partySize } = useController({ control, name: 'partySize' });
  // Validity of the typed phone, reported by PhonePicker (5–20 chars + a real,
  // assignable number). Tracked separately so submit can gate on it.
  const [phoneValid, setPhoneValid] = useState(false);
  const submitting = useRef(false);
  const limits = useBookingLimits(date.value, time.value, partySize.value);
  useUnsavedGuard(hasDraft(form.watch(), start));

  const submit = (data: StaffReservationCreateBody) => {
    if (!restaurantId || submitting.current) return;
    submitting.current = true;
    createMutation.mutate(
      { restaurantId, data },
      {
        onSuccess: () => {
          // The booking is saved - drop the draft flag so the Back below (and
          // any beforeunload) doesn't prompt to discard what we just persisted.
          setUnsavedDirty(false);
          f7.toast.show({ text: tr.createReservation.created, closeTimeout: 2000 });
          // Guarded: this fires from a mutation callback, so the hosting view can
          // already be gone (account switch / reloadAll / unmount mid-request).
          // `sectionRouter()` returns null there, making this a no-op instead of
          // throwing on `.router`.
          sectionRouter()?.back();
        },
        onError: () => f7.dialog.alert(tr.createReservation.createFailed),
        onSettled: () => {
          submitting.current = false;
        },
      },
    );
  };

  const handleSubmit = () => {
    const problem = createProblem(form.getValues(), phoneValid, !!restaurantId);
    if (problem) {
      f7.dialog.alert(tr.createReservation[problem]);
      return;
    }
    void form.handleSubmit(
      (data) => limits.confirmThen(() => submit(data)),
      () => f7.dialog.alert(tr.createReservation.createFailed),
    )();
  };

  return (
    <Page name="create-reservation">
      <Navbar>
        <NavLeft>
          <F7Link back>{tr.back}</F7Link>
        </NavLeft>
        <NavTitle>{tr.reservations.newReservation}</NavTitle>
        <NavRight>
          {createMutation.isPending ? (
            <Preloader size={20} />
          ) : (
            <F7Link onClick={handleSubmit}>{tr.createReservation.submit}</F7Link>
          )}
        </NavRight>
      </Navbar>

      <CreateGuestSection control={control} onPhoneValid={setPhoneValid} />

      <BlockTitle>{tr.createReservation.sectionBooking}</BlockTitle>
      <BookingWhenList
        date={date}
        time={time}
        partySize={partySize}
        maxPartySize={limits.maxPartySize}
        partyOverLimit={limits.partyOverLimit}
        showSlotFull={limits.showSlotFull}
      />

      <CreateDetailsSection control={control} />
    </Page>
  );
}

export default CreateReservationPage;
